import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import SortIcon from "../../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../../components/CommonComponents/Table/Table";
import { useDebounce } from "../../../hooks/common";
import EditButton from "../../../components/CommonComponents/EditComponent/EditComponent";
import { formatDate } from "../../../utils/date";
import {
    useGetBlockedPlots,
    useBlockedPlotsCount,
} from "../../../hooks/blockedPlot.hooks"
import {PaginatorForRDT} from "../../../components/CommonComponents/Paginator/Paginator";
import PageHeader from "../../../components/pageHeader/pageHeader";

type TBlockedPlot = {
    id: number;
    mauja: string;
    khasaraNo: string;
    plotNo: string;
    active: boolean;
    modifiedBy: string;
    updatedAt: string;
};

export default function BlockedPlotMapping() {
    const limit = 10;
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const search = useDebounce(searchQuery);

    const [{ sortKey, sortOrder }, setSort] = useState({
        sortKey: "",
        sortOrder: 1,
    });

    const { data: count, isLoading: countLoading } =
        useBlockedPlotsCount({ search });

    const { data, isLoading } = useGetBlockedPlots({
        page,
        limit,
        search,
        sort: sortKey,
        sortVal: sortOrder,
    });

    const columns = useMemo(
        (): TableColumn<TBlockedPlot>[] => [
            {
                name: "Sr No.",
                selector: (_, i) =>
                    ((page - 1) * limit + (i ?? 0) + 1)
                        .toString()
                        .padStart(3, "0"),
                center: true,
                grow: 0.5,
            },
            {
                name: "Mauja",
                selector: (d) => d.mauja,
                sortable: true,
                sortField: "mauja",
                center: true,
            },
            {
                name: "Khasara No.",
                selector: (d) => d.khasaraNo,
                sortable: true,
                sortField: "khasaraNo",
                center: true,
            },
            {
                name: "Plot No.",
                selector: (d) => d.plotNo,
                sortable: true,
                sortField: "plotNo",
                center: true,
            },
            {
                name: "Status",
                sortable: true,
                sortField: "active",
                center: true,
                cell: (d) => (
                    <div
                        className={`${d.active ? "activeButtonCss" : "inactiveButtonCss"
                            } flex justify-center items-center`}
                    >
                        {d.active ? "Active" : "Inactive"}
                    </div>
                ),
            },
            {
                name: "Modified By",
                selector: (d) => d.modifiedBy,
                sortable: true,
                sortField: "modifiedBy",
                center: true,
            },
            {
                name: "Modified On",
                selector: (d) => formatDate(d.updatedAt),
                sortable: true,
                sortField: "updatedAt",
                center: true,
            },
            {
                name: "Action",
                center: true,
                grow: 0.5,
                cell: (d) => (
                    <EditButton
                        onClick={() =>
                            navigate(`/edit-blocked-plot-mapping/${d.id}`)
                        }
                    />
                ),
            },
        ],
        [navigate, page, limit]
    );

    return (
        <div className="p-4 md:p-8 overflow-auto">
            <PageHeader
                title="Blocked Plot Mapping"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showSearchButton={true}
                showAdditionalButton={true}
                showGridButton={false}
                buttonTitle="Add New Record"
                showFilterButton={false}
                onClick={() => { navigate("/add-blocked-plot-mapping") }}
            />
            <BoxComponent>
                <DataTable
                    pagination
                    paginationServer
                    className="mt-4 rounded-0"
                    columns={columns}
                    data={data?.data || []}
                    onChangePage={(p) => setPage(p)}
                    customStyles={tableCustomStyles}
                    sortIcon={<SortIcon />}
                    paginationTotalRows={count?.data || 0}
                    progressPending={isLoading || countLoading}
                    paginationComponent={PaginatorForRDT}
                    onSort={(col, dir) => {
                        setSort({
                            sortKey: col.sortField || "",
                            sortOrder: dir === "asc" ? 1 : -1,
                        });
                    }}
                />
            </BoxComponent>
        </div>
    );
}